import React, {useCallback, useEffect, useState} from "react";
import axiosInstance from "../../../AxiosInstance";
import styles from "../../../styles/main.module.css";
import Header from "../../components/header/header";
import SidebarProvider from "../../components/sidebar/sidebar";
import Title from "../../../components/title/title";
import {PrimaryButton} from "../../../components/input/inputs";
import Loading from "../../../components/loading/loading";
import TableComponent from "../../../components/table/table";
import Pagination from "../../../components/ajonjolib/utils/pagination";
import {faEdit, faTrash} from "@fortawesome/free-solid-svg-icons";
import DeleteModal from "../../../components/delete_modal/delete_modal";
import CreateUserModal from "./modals/create_modal";

const PAGE_SIZE = 25;

/** Misma forma que espera la tabla plana (columna RUC, flags numéricos para formularios). */
function mapUsersFromApi(results) {
    if (!Array.isArray(results)) {
        return [];
    }
    return results.map((user) => ({
        ...user,
        ruc: user.company?.ruc ?? user.ruc,
        is_staff: user.is_staff ? 1 : 0,
        is_active: user.is_active ? 1 : 0,
        can_final_approve_invoice: user.can_final_approve_invoice ? 1 : 0,
        can_pre_approve_invoice: user.can_pre_approve_invoice ? 1 : 0,
        password: undefined,
    }));
}

export default function UserSettings() {
    const [showCreate, setShowCreate] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [editing, setEditing] = useState(false);
    const [deleteId, setDeleteId] = useState(0);
    const [users, setUsers] = useState([]);
    const [userProfiles, setUserProfiles] = useState([]);
    const [form, setForm] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    /** Solo la primera carga: si pasáramos a false en cada página, el Pagination se desmonta y al remontar puede fallar el salto de página. */
    const [initialLoading, setInitialLoading] = useState(true);

    const preDelete = (id) => {
        setDeleteId(id);
        setShowDelete(true);
    }

    const fetchUsers = useCallback((page = 1) => {
        axiosInstance
            .get(`user/?page=${page}`)
            .then((response) => {
                const data = response.data;
                const results = data.results;
                if (!Array.isArray(results)) {
                    setUsers([]);
                    setPageCount(1);
                    setInitialLoading(false);
                    return;
                }
                setUsers(mapUsersFromApi(results));
                const count = typeof data.count === 'number' ? data.count : results.length;
                setPageCount(Math.max(1, Math.ceil(count / PAGE_SIZE)));
                setInitialLoading(false);
            })
            .catch(() => {
                setUsers([]);
                setPageCount(1);
                setInitialLoading(false);
            });
    }, []);

    const load = () => {
        setCurrentPage(1);
        fetchUsers(1);
    };

    useEffect(() => {
        fetchUsers(1);
    }, [fetchUsers]);

    const cols = [
        {title: "RUC", internal: "ruc"},
        {title: "Email", internal: "email"},
        {title: "Nombre", internal: "first_name"},
        {title: "Apellido", internal: "last_name"},
        {
            title: 'Acciones',
            buttons: [
                {
                    icon: faEdit,
                    color: "#44A04A",
                    onClick: (id) => {
                        const found = users.find((row) => row.id === id);
                        if (!found) return;
                        setShowCreate(true);
                        found.ruc = found.company?.ruc ?? found.ruc;
                        found.is_staff = found.is_staff ? 1 : 0;
                        found.is_active = found.is_active ? 1 : 0;
                        found.can_final_approve_invoice = found.can_final_approve_invoice ? 1 : 0;
                        found.can_pre_approve_invoice = found.can_pre_approve_invoice ? 1 : 0;
                        found.password = undefined;
                        setForm(found);
                        setEditing(true);
                    },
                },
                {
                    icon: faTrash,
                    color: "#DC3545",
                    onClick: preDelete,
                },
            ],
        },
    ];

    const deleteObj = () => {
        axiosInstance.delete('user/' + deleteId + '/').then(() => {
            window.location.reload();
        });
    };

    useEffect(() => {
        axiosInstance.get('user_profile/').then((response) => {
            setUserProfiles(response.data.results);
        });
    }, []);

    const [sidebarOpen, setSidebarOpen] = useState(true);

    return (
        <div className={styles.mainContainer}>
            <Header setSidebarOpen={setSidebarOpen}/>
            <SidebarProvider index={5} isOpen={sidebarOpen}/>

            <div className={styles.container} style={!sidebarOpen ? {marginLeft: '80px'} : {marginLeft: '285px'}}>
                <Title name={'Usuarios'}/>

                <CreateUserModal roles={userProfiles} form={form} setForm={setForm} show={showCreate} setter={setShowCreate} reload={load} editing={editing} />

                <DeleteModal show={showDelete} setter={setShowDelete} name={'el usuario'} buttonName={'Guardar'} onClick={deleteObj}/>

                <div className={`${styles.secondaryContainer}`}>
                    <div className={'d-flex justify-content-end'}>
                        <PrimaryButton name={'Agregar usuario'} onClick={() => {
                            setForm({ is_active: 1, is_staff: 1, role: 1, can_final_approve_invoice: 0, can_pre_approve_invoice: 0 });
                            setShowCreate(true);
                            setEditing(false);
                        }}/>
                    </div>

                    {initialLoading ? (
                        <Loading/>
                    ) : users.length === 0 ? (
                        <div>No hay usuarios registrados.</div>
                    ) : (
                        <React.Fragment>
                            <TableComponent
                                suppressFetch
                                cols={cols}
                                rows={users}
                                withIndex
                                currentPage={currentPage}
                                pageSize={PAGE_SIZE}
                            />
                            <Pagination
                                currentPage={currentPage}
                                count={pageCount}
                                arrows
                                className="mt-3"
                                onInput={(page) => {
                                    setCurrentPage(page);
                                    fetchUsers(page);
                                }}
                            />
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    );
}
